import { useRef, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

const DynamicCamera = ({ audioData, vibeConfig, enabled = true }) => {
  const { camera } = useThree();
  const targetRef = useRef(new THREE.Vector3(0, 0, 0));
  const currentPositionRef = useRef(new THREE.Vector3(0, 0, 12));
  const baseRadiusRef = useRef(15);
  const angleRef = useRef(0);
  const verticalAngleRef = useRef(0);
  const lastBeatTimeRef = useRef(0);
  const beatPatternRef = useRef(0);
  
  // Physics-based camera system
  const velocityRef = useRef(new THREE.Vector3(0, 0, 0));
  const accelerationRef = useRef(new THREE.Vector3(0, 0, 0));
  const joltVelocityRef = useRef(new THREE.Vector3(0, 0, 0));
  const springForceRef = useRef(new THREE.Vector3(0, 0, 0));
  const dampingRef = useRef(0.92); // Damping factor for physics
  const springStrengthRef = useRef(0.15); // Spring strength for returning to target
  
  // Look-at physics
  const lookAtVelocityRef = useRef(new THREE.Vector3(0, 0, 0));
  const lookAtTargetRef = useRef(new THREE.Vector3(0, 0, 0));
  const currentLookAtRef = useRef(new THREE.Vector3(0, 0, 0));
  
  useEffect(() => {
    if (enabled) {
      // Initialize camera position
      camera.position.copy(currentPositionRef.current);
      camera.lookAt(targetRef.current);
    }
  }, [camera, enabled]);

  useFrame((state) => {
    if (!enabled || !audioData) return;

    const time = state.clock.getElapsedTime();
    const deltaTime = state.clock.getDelta();
    
    // Much less sensitive beat detection
    const beatThreshold = 0.9; // Much higher threshold - only very strong beats
    const isBeat = audioData.peak > beatThreshold;
    
    if (isBeat && time - lastBeatTimeRef.current > 2.0) { // Much longer cooldown - 2 seconds
      lastBeatTimeRef.current = time;
      beatPatternRef.current = (beatPatternRef.current + 1) % 4;
      
      // Physics-based beat jolts - reduced strength
      const joltStrength = audioData.peak * 1; // Reduced from 2
      const randomDirection = new THREE.Vector3(
        (Math.random() - 0.5) * joltStrength,
        (Math.random() - 0.5) * joltStrength * 0.3, // Reduced from 0.5
        (Math.random() - 0.5) * joltStrength * 0.2  // Reduced from 0.3
      );
      
      // Add jolt to velocity for physics-based movement
      joltVelocityRef.current.add(randomDirection);
      
      // Look-at jolt - reduced
      const lookAtJolt = new THREE.Vector3(
        (Math.random() - 0.5) * joltStrength * 0.3, // Reduced from 0.5
        (Math.random() - 0.5) * joltStrength * 0.2, // Reduced from 0.3
        0
      );
      lookAtVelocityRef.current.add(lookAtJolt);
    }
    
    // Much more subtle base orbital movement
    const baseSpeed = 0.05; // Drastically reduced from 0.2
    const audioSpeedMultiplier = 1 + audioData.average * 0.3 + audioData.peak * 0.5; // Much more subtle
    angleRef.current += baseSpeed * audioSpeedMultiplier * deltaTime;
    
    // Very gentle vertical movement
    const verticalSpeed = 0.02; // Drastically reduced from 0.1
    const verticalMultiplier = 1 + audioData.mid * 0.2; // Much more subtle
    verticalAngleRef.current += verticalSpeed * verticalMultiplier * deltaTime;
    
    // Subtle radius changes only
    const baseRadius = baseRadiusRef.current;
    const radiusVariation = audioData.bass * 2 + audioData.peak * 1; // Much more subtle
    const beatZoom = isBeat ? -1 : 0; // Very subtle zoom
    const currentRadius = Math.max(12, baseRadius + radiusVariation + beatZoom); // Safer distance
    
    // Calculate sophisticated camera position
    const horizontalRadius = currentRadius * Math.cos(verticalAngleRef.current * 0.3);
    const targetX = Math.cos(angleRef.current) * horizontalRadius;
    const targetZ = Math.sin(angleRef.current) * horizontalRadius;
    
    // Very subtle height changes
    const baseHeight = Math.sin(verticalAngleRef.current) * 2; // Much more subtle
    const audioHeight = audioData.mid * 1 + audioData.treble * 0.5; // Very subtle
    const beatHeight = isBeat ? Math.random() * 0.5 - 0.25 : 0; // Barely noticeable
    const targetY = baseHeight + audioHeight + beatHeight;
    
    let targetPosition = new THREE.Vector3(targetX, targetY, targetZ);
    
    // Physics-based camera movement
    // Calculate spring force towards target
    springForceRef.current.copy(targetPosition).sub(currentPositionRef.current).multiplyScalar(springStrengthRef.current);
    
    // Add continuous audio-based micro forces
    const audioForce = new THREE.Vector3(
      Math.sin(time * 3) * audioData.treble * 0.02,
      Math.cos(time * 2.5) * audioData.mid * 0.015,
      Math.sin(time * 4) * audioData.bass * 0.01
    );
    
    // Update acceleration with spring force, audio force, and jolt velocity
    accelerationRef.current.copy(springForceRef.current).add(audioForce).add(joltVelocityRef.current);
    
    // Update velocity with acceleration
    velocityRef.current.add(accelerationRef.current.clone().multiplyScalar(deltaTime));
    
    // Apply damping to velocity
    velocityRef.current.multiplyScalar(dampingRef.current);
    
    // Apply damping to jolt velocity
    joltVelocityRef.current.multiplyScalar(0.85);
    
    // Update position with velocity
    currentPositionRef.current.add(velocityRef.current.clone().multiplyScalar(deltaTime));
    
    // Apply position to camera
    camera.position.copy(currentPositionRef.current);
    
    // Physics-based look-at system
    const baseLookAtOffset = new THREE.Vector3(
      Math.sin(time * 0.1 + audioData.bass * 1) * audioData.mid * 0.3,
      Math.cos(time * 0.08 + audioData.treble * 1) * audioData.bass * 0.2,
      Math.sin(time * 0.12) * audioData.treble * 0.1
    );
    
    // Update look-at target
    lookAtTargetRef.current.copy(baseLookAtOffset);
    
    // Physics-based look-at movement
    const lookAtSpringForce = lookAtTargetRef.current.clone().sub(currentLookAtRef.current).multiplyScalar(0.1);
    lookAtVelocityRef.current.add(lookAtSpringForce.multiplyScalar(deltaTime));
    lookAtVelocityRef.current.multiplyScalar(0.9); // Damping
    
    currentLookAtRef.current.add(lookAtVelocityRef.current.clone().multiplyScalar(deltaTime));
    
    camera.lookAt(currentLookAtRef.current);
    
    // Minimal FOV changes like professional visualizers
    const baseFOV = 75;
    const fovVariation = audioData.peak * 3 + audioData.bass * 2; // Much more subtle
    const beatFOVBoost = isBeat ? 1 : 0; // Barely noticeable
    camera.fov = Math.max(70, Math.min(85, baseFOV + fovVariation + beatFOVBoost)); // Much narrower range
    camera.updateProjectionMatrix();
  });

  return null;
};

export default DynamicCamera;